import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Copy, Check, Search, Users, Eye, Pencil, X, UserPlus, MousePointerClick, TrendingUp, ExternalLink, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

const SITE_ORIGIN = 'https://say-it-now.app';

interface InfluencerProfile {
  id: string;
  name: string;
  display_label: string;
  email: string;
  ref_code: string;
  assigned_plan: string;
  free_credits: number;
  free_access_enabled: boolean;
  status: string;
  notes: string;
  linked_user_id: string | null;
  created_at: string;
  updated_at: string;
}

interface ReferredUser {
  email: string;
  full_name: string | null;
  created_at: string;
  announcement_credits: number | null;
  highest_bundle_purchased: string | null;
}

const emptyForm: Omit<InfluencerProfile, 'id' | 'created_at' | 'updated_at' | 'linked_user_id'> = {
  name: '',
  display_label: '',
  email: '',
  ref_code: '',
  assigned_plan: 'legacy',
  free_credits: 25,
  free_access_enabled: true,
  status: 'active',
  notes: '',
};

export default function AdminInfluencerManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [profiles, setProfiles] = useState<InfluencerProfile[]>([]);
  const [actualCredits, setActualCredits] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Detail view
  const [detailId, setDetailId] = useState<string | null>(null);
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([]);
  const [loadingReferred, setLoadingReferred] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [signupCount, setSignupCount] = useState(0);

  // Copy feedback
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      if (!data) { navigate('/dashboard'); return; }
      setIsAdmin(true);
    });
  }, [user, navigate]);

  const fetchProfiles = async () => {
    const { data } = await supabase
      .from('influencer_profiles' as any)
      .select('*')
      .order('created_at', { ascending: false });
    if (data) {
      const typedData = data as unknown as InfluencerProfile[];
      setProfiles(typedData);

      // Fetch actual remaining credits for linked users
      const linkedUserIds = typedData
        .filter(p => p.linked_user_id)
        .map(p => p.linked_user_id as string);

      if (linkedUserIds.length > 0) {
        const { data: creditsData } = await supabase
          .from('user_credits')
          .select('user_id, announcement_credits')
          .in('user_id', linkedUserIds);

        if (creditsData) {
          const creditsMap: Record<string, number> = {};
          for (const row of creditsData) {
            creditsMap[row.user_id] = row.announcement_credits;
          }
          setActualCredits(creditsMap);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchProfiles();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    return profiles.filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.email.toLowerCase().includes(search.toLowerCase()) || p.ref_code.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [profiles, search, statusFilter]);

  const generateRefCode = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 15) || 'inf' + Math.random().toString(36).slice(2, 8);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: InfluencerProfile) => {
    setEditId(p.id);
    setForm({
      name: p.name,
      display_label: p.display_label,
      email: p.email,
      ref_code: p.ref_code,
      assigned_plan: p.assigned_plan,
      free_credits: p.free_credits,
      free_access_enabled: p.free_access_enabled,
      status: p.status,
      notes: p.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.email || !form.ref_code) {
      toast({ title: 'Missing fields', description: 'Name, email, and referral code are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const { error } = await supabase
          .from('influencer_profiles' as any)
          .update(form as any)
          .eq('id', editId);
        if (error) throw error;
        toast({ title: 'Updated', description: `${form.name} updated successfully.` });
      } else {
        // Also create/sync influencer_codes entry for click tracking
        const { error } = await supabase
          .from('influencer_profiles' as any)
          .insert(form as any);
        if (error) throw error;

        // Sync to influencer_codes for click tracking
        await supabase.from('influencer_codes').upsert(
          { ref_code: form.ref_code, label: `${form.name} - ${form.display_label || 'Influencer'}` } as any,
          { onConflict: 'ref_code' } as any
        );

        toast({ title: 'Created', description: `${form.name} added as influencer.` });
      }
      setDialogOpen(false);
      fetchProfiles();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast({ title: 'Copied!', description: `${field} copied to clipboard.` });
  };

  const openDetail = async (p: InfluencerProfile) => {
    setDetailId(p.id);
    setLoadingReferred(true);

    // Get click/signup counts from influencer_codes
    const { data: codeData } = await supabase
      .from('influencer_codes')
      .select('clicks, signups')
      .eq('ref_code', p.ref_code)
      .maybeSingle();
    setClickCount(codeData?.clicks ?? 0);
    setSignupCount(codeData?.signups ?? 0);

    // Get referred users via referrals table (where referral code matches)
    const { data: refData } = await supabase.rpc('admin_search_users', { p_search: '' });
    // Filter to users referred by this influencer's code - check referrals table
    const { data: referralRows } = await supabase
      .from('referrals')
      .select('referred_user_id, referred_email, referred_name, created_at');

    // For now, show users who signed up via this ref code
    // We'll match from the credit_transactions metadata
    setReferredUsers([]);
    setLoadingReferred(false);
  };

  const detailProfile = profiles.find(p => p.id === detailId);

  const getShareMessage = (p: InfluencerProfile) => {
    return `Hey ${p.name}! 🎉\n\nYou're set up as an influencer for Say It NOW.\n\n📝 Sign up here: ${SITE_ORIGIN}/signup\n🔗 Your referral link to share: ${SITE_ORIGIN}/?ref=${p.ref_code}\n\nYour account will be automatically activated with ${p.assigned_plan} plan and ${p.free_credits} free credits when you sign up with this email: ${p.email}\n\nShare your referral link with your audience so we can track your impact!`;
  };

  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const planLabel = (p: string) => p.charAt(0).toUpperCase() + p.slice(1);

  // Detail View
  if (detailProfile) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-background/95 backdrop-blur">
          <div className="container flex h-14 items-center gap-4">
            <button onClick={() => setDetailId(null)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back to List</span>
            </button>
            <h1 className="text-lg font-bold text-foreground">{detailProfile.name}</h1>
          </div>
        </header>
        <main className="container py-6 space-y-6 max-w-3xl">
          {/* Profile Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <p className="font-medium">{detailProfile.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p className="font-medium">{detailProfile.email}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Plan:</span>
                  <Badge variant="secondary">{planLabel(detailProfile.assigned_plan)}</Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Remaining Credits:</span>
                  <p className="font-medium">
                    {detailProfile.linked_user_id && actualCredits[detailProfile.linked_user_id] !== undefined
                      ? actualCredits[detailProfile.linked_user_id]
                      : `${detailProfile.free_credits} (assigned)`}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant={detailProfile.status === 'active' ? 'default' : 'outline'}>{detailProfile.status}</Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Free Access:</span>
                  <p className="font-medium">{detailProfile.free_access_enabled ? '✅ Enabled' : '❌ Disabled'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Account Linked:</span>
                  <p className="font-medium">{detailProfile.linked_user_id ? '✅ Yes' : '⏳ Not yet signed up'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Created:</span>
                  <p className="font-medium">{format(new Date(detailProfile.created_at), 'MMM d, yyyy')}</p>
                </div>
              </div>
              {detailProfile.notes && (
                <div>
                  <span className="text-muted-foreground text-sm">Notes:</span>
                  <p className="text-sm mt-1">{detailProfile.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Copy Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Copy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { label: 'Referral Link', value: `${SITE_ORIGIN}/?ref=${detailProfile.ref_code}`, key: 'ref-link' },
                { label: 'Referral Code', value: detailProfile.ref_code, key: 'ref-code' },
                { label: 'Email', value: detailProfile.email, key: 'email' },
                { label: 'Setup Message', value: getShareMessage(detailProfile), key: 'message' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/30">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="text-sm font-mono truncate">{item.key === 'message' ? 'Full setup message...' : item.value}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(item.value, item.label)} className="shrink-0">
                    {copiedField === item.label ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Performance Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <MousePointerClick className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold">{clickCount}</p>
                <p className="text-xs text-muted-foreground">Link Clicks</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <UserPlus className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold">{signupCount}</p>
                <p className="text-xs text-muted-foreground">Signups</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <TrendingUp className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold">{clickCount > 0 ? ((signupCount / clickCount) * 100).toFixed(1) : '0'}%</p>
                <p className="text-xs text-muted-foreground">Conversion</p>
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openEdit(detailProfile)}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Main List View
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Admin Hub</span>
          </Link>
          <h1 className="text-lg font-bold text-foreground">Influencer Manager</h1>
        </div>
      </header>

      <main className="container py-6 space-y-6 max-w-5xl">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name, email, code..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <Filter className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openCreate} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Add Influencer
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold">{profiles.length}</p>
              <p className="text-xs text-muted-foreground">Total Influencers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <UserPlus className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold">{profiles.filter(p => p.linked_user_id).length}</p>
              <p className="text-xs text-muted-foreground">Signed Up</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold">{profiles.filter(p => p.status === 'active').length}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-sm text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-10 w-10 text-muted-foreground mx-auto mb-2 opacity-50" />
                <p className="text-sm text-muted-foreground">No influencers found. Click "Add Influencer" to create one.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Signed Up</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(p => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="text-sm">{p.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-xs">{p.ref_code}</span>
                            <button onClick={() => copyToClipboard(`${SITE_ORIGIN}/?ref=${p.ref_code}`, `${p.name} link`)} className="text-muted-foreground hover:text-foreground">
                              {copiedField === `${p.name} link` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs">{planLabel(p.assigned_plan)}</Badge></TableCell>
                        <TableCell className="text-right">
                          {p.linked_user_id && actualCredits[p.linked_user_id] !== undefined
                            ? actualCredits[p.linked_user_id]
                            : p.free_credits}
                        </TableCell>
                        <TableCell>
                          <Badge variant={p.status === 'active' ? 'default' : 'outline'} className="text-xs">{p.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {p.linked_user_id ? (
                            <Badge variant="default" className="text-xs bg-green-600">✓ Yes</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Pending</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" onClick={() => openDetail(p)} title="View details">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => openEdit(p)} title="Edit">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => copyToClipboard(getShareMessage(p), `${p.name} message`)} title="Copy setup message">
                              {copiedField === `${p.name} message` ? <Check className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Influencer' : 'Add Influencer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input value={form.name} onChange={e => {
                setForm(f => ({ ...f, name: e.target.value }));
                if (!editId && !form.ref_code) {
                  setForm(f => ({ ...f, name: e.target.value, ref_code: generateRefCode(e.target.value) }));
                }
              }} placeholder="LaToya Smith" />
            </div>
            <div>
              <label className="text-sm font-medium">Display Label</label>
              <Input value={form.display_label} onChange={e => setForm(f => ({ ...f, display_label: e.target.value }))} placeholder="Content Creator" />
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="latoya@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium">Referral Code *</label>
              <Input value={form.ref_code} onChange={e => setForm(f => ({ ...f, ref_code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))} placeholder="latoya" />
              <p className="text-xs text-muted-foreground mt-1">Link: {SITE_ORIGIN}/?ref={form.ref_code || '...'}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Assigned Plan</label>
                <Select value={form.assigned_plan} onValueChange={v => setForm(f => ({ ...f, assigned_plan: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="connection">Connection</SelectItem>
                    <SelectItem value="legacy">Legacy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Free Credits</label>
                <Input type="number" value={form.free_credits} onChange={e => setForm(f => ({ ...f, free_credits: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium">Free Account Access</label>
                <p className="text-xs text-muted-foreground">Auto-provision plan on signup</p>
              </div>
              <Switch checked={form.free_access_enabled} onCheckedChange={v => setForm(f => ({ ...f, free_access_enabled: v }))} />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update' : 'Create Influencer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
